import { create } from "zustand";
import toast from "react-hot-toast";
import { axiosInstance } from "../lib/axios";

export interface ProposalSection {
  id: string;
  type: string;
  layout: string;
  title: string;
  order: number;
  content: any;
}

export interface ProposalCustomization {
  primaryColor: string;
  accentColor: string;
  backgroundColor: string;
  darkSectionColor: string;
  headingStyle: string;
  sectionStyle: string;
  fontFamily: string;
}

export interface Proposal {
  _id: string;
  title: string;
  category: string;
  brand: string;
  template?: "modern" | "classic" | "minimal" | "infographic";
  proposalType?: string;
  status?: "Draft" | "In Review" | "Ready to Publish" | "Published";
  version?: number;
  publishedAt?: string | null;
  publishedUrl?: string;
  clientInfo?: { name?: string; company?: string; email?: string; phone?: string; website?: string };
  fullName?: string;
  tagline?: string;
  profileImage?: string;
  stats?: { value: string; label: string }[];
  links?: { label: string; url: string }[];
  customization?: ProposalCustomization;
  sections: ProposalSection[];
  publicToken: string;
  createdAt?: string;
  updatedAt?: string;
  createdBy?: { _id: string; fullName?: string; email?: string } | string;
  upworkAccount?: { _id: string; name?: string } | string | null;
  client?: { _id: string; name?: string; company?: string } | string | null;
  viewCount?: number;
  firstViewedAt?: string | null;
  lastViewedAt?: string | null;
}

export interface ProposalFilters {
  bdId?: string;
  upworkAccount?: string;
  from?: string;
  to?: string;
  sort?: "latest" | "oldest";
}

export interface ProposalStats {
  total: number;
  totalViews: number;
  viewedCount: number;
  mostUsedTemplate: string;
  topCategory: string;
  topTemplateByViews: string;
  byCategory: { key: string; label: string; count: number; views: number }[];
  byTemplate: { key: string; label: string; count: number; views: number }[];
  byBd: { key: string; label: string; count: number; views: number }[];
  overTime: { month: string; count: number }[];
}

interface ProposalState {
  proposals: Proposal[];
  selectedProposal: Proposal | null;

  isFetchingProposals: boolean;
  isCreatingProposal: boolean;
  isUpdatingProposal: boolean;
  isDeletingProposal: boolean;

  stats: ProposalStats | null;
  isFetchingStats: boolean;

  fetchProposals: (filters?: ProposalFilters) => Promise<void>;
  fetchStats: (filters?: ProposalFilters) => Promise<void>;
  fetchProposalById: (id: string) => Promise<Proposal | null>;
 createProposal: (data: any) => Promise<Proposal | null>;
  fetchReview: (id: string) => Promise<any | null>;
  publishProposal: (id: string) => Promise<any | null>;
  isPublishing: boolean;
  updateProposal: (id: string, data: Partial<Proposal>) => Promise<void>;
  duplicateProposal: (id: string) => Promise<Proposal | null>;
  deleteProposal: (id: string) => Promise<void>;
  setSelectedProposal: (proposal: Proposal | null) => void;
}

export const useProposalStore = create<ProposalState>((set, get) => ({
  proposals: [],
  selectedProposal: null,

  isFetchingProposals: false,
  isCreatingProposal: false,
  isUpdatingProposal: false,
  isDeletingProposal: false,
  isPublishing: false,
  stats: null,
  isFetchingStats: false,

  fetchProposals: async (filters = {}) => {
    set({ isFetchingProposals: true });
    try {
      const params: Record<string, string> = {};
      if (filters.bdId) params.bdId = filters.bdId;
      if (filters.upworkAccount) params.upworkAccount = filters.upworkAccount;
      if (filters.from) params.from = filters.from;
      if (filters.to) params.to = filters.to;
      if (filters.sort) params.sort = filters.sort;
      const res = await axiosInstance.get<Proposal[]>("/proposals", { params });
      set({ proposals: res.data });
    } catch (error: any) {
      toast.error(error.response?.data?.message || "Failed to fetch proposals");
    } finally {
      set({ isFetchingProposals: false });
    }
  },

  fetchStats: async (filters = {}) => {
    set({ isFetchingStats: true });
    try {
      const params: Record<string, string> = {};
      if (filters.bdId) params.bdId = filters.bdId;
      if (filters.upworkAccount) params.upworkAccount = filters.upworkAccount;
      const res = await axiosInstance.get<ProposalStats>("/proposals/stats", { params });
      set({ stats: res.data });
    } catch {
      /* stats are non-critical — fail silently */
    } finally {
      set({ isFetchingStats: false });
    }
  },

  duplicateProposal: async (id) => {
    try {
      const res = await axiosInstance.post<Proposal>(`/proposals/${id}/duplicate`);
      toast.success("Proposal duplicated — opening in builder");
      return res.data;
    } catch (error: any) {
      toast.error(error.response?.data?.message || "Failed to duplicate proposal");
      return null;
    }
  },

  fetchProposalById: async (id) => {
    try {
      const res = await axiosInstance.get<Proposal>(`/proposals/${id}`);
      return res.data;
    } catch (error: any) {
      toast.error(error.response?.data?.message || "Failed to fetch proposal");
      return null;
    }
  },

  createProposal: async (data) => {
    set({ isCreatingProposal: true });
    try {

      const res = await axiosInstance.post("/proposals", data);
      set({ proposals: [res.data, ...get().proposals] });
      toast.success("Proposal created successfully");
      return res.data as Proposal;
    } catch (error: any) {
      toast.error(error.response?.data?.message || "Failed to create proposal");
      return null;
    } finally {
      set({ isCreatingProposal: false });
    }
  },

  // Everything the review screen needs: proposal, brand, validation, share URL.
  fetchReview: async (id) => {
    try {
      const res = await axiosInstance.get(`/proposals/${id}/review`);
      return res.data;
    } catch (error: any) {
      toast.error(error.response?.data?.message || "Failed to load the proposal for review");
      return null;
    }
  },

  // Publishing is refused server-side while required information is missing;
  // the validation that comes back is what the review panel then shows.
  publishProposal: async (id) => {
    set({ isPublishing: true });
    try {
      const res = await axiosInstance.post(`/proposals/${id}/publish`);
      toast.success(res.data?.message || "Proposal published");
      return res.data;
    } catch (error: any) {
      const data = error.response?.data;
      toast.error(data?.message || "Failed to publish the proposal");
      return data?.validation ? { error: true, validation: data.validation } : null;
    } finally {
      set({ isPublishing: false });
    }
  },

  updateProposal: async (id, data) => {
    set({ isUpdatingProposal: true });
    try {
      const res = await axiosInstance.put<Proposal>(
        `/proposals/${id}`,
        data
      );

      set({
        proposals: get().proposals.map((p) =>
          p._id === id ? res.data : p
        ),
      });

      toast.success("Proposal updated successfully");
    } catch (error: any) {
      toast.error(error.response?.data?.message || "Failed to update proposal");
    } finally {
      set({ isUpdatingProposal: false });
    }
  },

  deleteProposal: async (id) => {
    set({ isDeletingProposal: true });
    try {
      await axiosInstance.delete(`/proposals/${id}`);
      set({
        proposals: get().proposals.filter((p) => p._id !== id),
      });
      toast.success("Proposal deleted successfully");
    } catch (error: any) {
      toast.error(error.response?.data?.message || "Failed to delete proposal");
    } finally {
      set({ isDeletingProposal: false });
    }
  },

  setSelectedProposal: (proposal) =>
    set({ selectedProposal: proposal }),
}));
